import type { Virtualizer } from '@tanstack/react-virtual'
import { useCallback, useMemo, useRef, useState } from 'react'
import { fail } from '#app/assert'
import { useAppDispatch, useAppSelector } from '#app/store'
import { addViewHighlight, type SnapshotId, type TruckConfiguration } from '#app/store/slices/configurationSlice'
import { type ConfigurationEnrichmentData, enrichConfiguration } from '#app/store/slices/enrichConfiguration'
import type { ComparisonDisplayMode } from '../Comparison/ComparisonTableContext'
import { useComparisonTable } from '../Comparison/useComparisonTable'
import { type ConfigurationDiff, useConfigurationDifference } from '../Comparison/useConfigurationDifference'
import type { ComparisonDataEntry, FamilyDataEntry } from '../Comparison/useLinearizeComparisonData'
import type { FilterState } from '../Details/Filter/FilterState'
import { useConfigurationHoveredEntry, useScrollRecovery } from '../Details/useDetailsContext'

// @TODO: I have no clue what 'severe' is currently
// type ChangesDisplayMode = 'differences' | 'all' // | 'severe'

const useComparedState = (configuration: TruckConfiguration | undefined, baseId: SnapshotId, comparedId: SnapshotId) => {
  const favorites = useAppSelector((x) => x.userData.favorites)
  return useMemo(() => {
    if (!configuration) {
      return {}
    }
    const base = configuration.snapshots.find((x) => x.id === baseId)
    const snapshot = configuration.snapshots.find((x) => x.id === comparedId)
    if (!base || !snapshot) {
      return {}
    }
    const primaryState: TruckConfiguration = {
      ...configuration,
      productGroups: base.productGroups,
      initialSelection: base.initialSelection,
      configuredFamilies: base.configuredFamilies,
      lastChange: { when: base.when, version: base.version },
    }
    const comparedState: TruckConfiguration = {
      ...configuration,
      productGroups: snapshot.productGroups,
      initialSelection: snapshot.initialSelection,
      configuredFamilies: snapshot.configuredFamilies,
      lastChange: { when: snapshot.when, version: snapshot.version },
    }

    const primaryStateEnrichment = enrichConfiguration(
      primaryState.productGroups,
      primaryState.configuredFamilies,
      primaryState.initialSelection,
      primaryState.marketData,
      favorites,
      primaryState.apiConfiguration.mode ?? 'writable',
      primaryState.step.system,
      primaryState.apiConfiguration,
      primaryState.optionalEquipment,
      primaryState.tg3?.tg3ConversionInsightsLookup,
      { currentConfigurationVersion: primaryState.version, compatibility: primaryState.compatibility },
    )
    const comparedStateEnrichment = enrichConfiguration(
      comparedState.productGroups,
      comparedState.configuredFamilies,
      comparedState.initialSelection,
      comparedState.marketData,
      favorites,
      comparedState.apiConfiguration.mode ?? 'writable',
      comparedState.step.system,
      comparedState.apiConfiguration,
      comparedState.optionalEquipment,
      comparedState.tg3?.tg3ConversionInsightsLookup,
      { currentConfigurationVersion: comparedState.version, compatibility: comparedState.compatibility },
    )

    return {
      base: {
        configuration: primaryState,
        enrichment: primaryStateEnrichment,
      },
      comparison: {
        configuration: comparedState,
        enrichment: comparedStateEnrichment,
      },
    }
  }, [configuration, favorites, comparedId, baseId])
}

export type ChangeTableContext = ReturnType<typeof useChangesTable>

export const useComparisonVirtualizer = (visibleEntries: ReturnType<typeof useComparisonTable>['displayedEntries']) => {
  const ref = useRef<Virtualizer<HTMLDivElement, Element>>(null)
  // debouncing the virtualizer setter
  const setVirtualizerRef = useCallback((virtualizer: Virtualizer<HTMLDivElement, Element>) => {
    if (ref.current !== virtualizer) {
      ref.current = virtualizer
    }
  }, [])

  // better scrolling function, so consumers don't need any knowledge of the inner workings
  const scrollTo = useCallback(
    (entry: ComparisonDataEntry) => {
      const index = visibleEntries.findIndex((x) => x.id === entry.id)
      if (index < 0) {
        fail(`Scroll Index had to be >= 0, tried to scroll to '${entry.id}'`)
        return
      }
      if (!ref.current) {
        return
      }
      ref.current.scrollToIndex(index, { align: 'start', behavior: 'auto' })
    },
    [visibleEntries],
  )
  return {
    ref,
    setVirtualizerRef,
    scrollTo,
  }
}

export interface ComparisonResult {
  base:
    | {
        configuration: TruckConfiguration
        enrichment: ConfigurationEnrichmentData
      }
    | undefined
  comparison:
    | {
        configuration: TruckConfiguration
        enrichment: ConfigurationEnrichmentData
      }
    | undefined
  diff: ConfigurationDiff | undefined
}

interface SnapshotComparisonResult extends ComparisonResult {
  availableChangesets: SnapshotId[]
  baseChangeSet: SnapshotId
  setBaseChangeSet: React.Dispatch<React.SetStateAction<SnapshotId>>
  comparedChangeSet: SnapshotId
  setComparedChangeSet: React.Dispatch<React.SetStateAction<SnapshotId>>
}

/** provides the comparison result of the first two snapshots */
export const useSnapShotComparison = (configuration: TruckConfiguration | undefined): SnapshotComparisonResult => {
  // from oldest to most recent
  const availableChangesets = useMemo(() => configuration?.snapshots.map((x) => x.id) ?? [], [configuration?.snapshots])
  // the finalizer can be only entered if there are no unsaved changes
  // this forces the user to save the MAN code to get into the finalizer, causing the most recent snapshot to be the current state
  // in here, declare the base changeset as the most recent one and then compare it with the previous version
  const [baseChangeSet, setBaseChangeSet] = useState<SnapshotId>(availableChangesets[availableChangesets.length - 1])
  // try finding the first step-2 snapshot, otherwise use the last snapshot
  const [comparedChangeSet, setComparedChangeSet] = useState<SnapshotId>(
    configuration?.snapshots.find((x) => x.view.startsWith('step-2'))?.id ?? availableChangesets[availableChangesets.length - 2],
  )

  const { base, comparison } = useComparedState(configuration, baseChangeSet, comparedChangeSet)
  const diff = useConfigurationDifference(base?.enrichment, comparison?.enrichment, true)

  return {
    availableChangesets,
    baseChangeSet,
    setBaseChangeSet,
    comparedChangeSet,
    setComparedChangeSet,
    base,
    comparison,
    diff,
  }
}

export function useChangesTable<T extends ComparisonResult = ComparisonResult>(comparisonResult: T) {
  const dispatch = useAppDispatch()
  // @TODO: Comparison Display mode is wrong here, temporary placed to make refactoring easier
  const [displayMode, setDisplayMode] = useState<ComparisonDisplayMode>('differences')
  const [filterState, setFilterState] = useState<FilterState>({})

  const { base, comparison, diff } = comparisonResult

  const hoveredEntry = useConfigurationHoveredEntry(base?.configuration, base?.enrichment, dispatch, 'changes')
  const setSearchQuery = useCallback(
    (searchQuery: string | undefined) => {
      setFilterState({ ...filterState, search: searchQuery })
    },
    [filterState],
  )

  const setHighlightEntry = (entry: FamilyDataEntry, side: 'base' | 'comparison') => {
    const family = side === 'base' ? entry.base : entry.comparison
    const variant = family?.selectedVariant
    if (!base?.configuration || !family || !variant) {
      return
    }
    dispatch(
      addViewHighlight({
        id: base.configuration.id,
        familyId: family.id,
        variantId: variant.id,
        scope: 'modal',
      }),
    )
  }

  const comparisonTable = useComparisonTable({ baseConfiguration: base, comparisonConfiguration: comparison, diff, displayMode, filterState })
  const virtualizer = useComparisonVirtualizer(comparisonTable.displayedEntries)
  useScrollRecovery(base?.configuration, comparisonTable.displayedEntries, virtualizer.ref, 'changes')

  return {
    displayMode,
    setDisplayMode,
    filter: {
      setSearchQuery,
      setFilterState,
      filterState,
    },
    ...comparisonTable,
    baseConfiguration: base,
    comparisonConfiguration: comparison,
    diff,
    hoveredEntry,
    setHighlightEntry,
    virtualizer,
    timestamps: {
      lastChange: base?.configuration.lastChange?.when,
      snapshotTime: comparison?.configuration.lastChange?.when,
    },
  }
}
